import { z } from 'zod'
import { oc } from '@orpc/contract'
import { CHART_RECORD_RESOURCE_TYPES } from '../../chart-records/handlers/chart-records.contract'
import { PATIENT_HEALTH_RECORD_SECTIONS } from './patient-health-record.contract'

/**
 * The patient's OWN health timeline, for the v2 portal's Health Timeline page (PD-1977).
 *
 * Self-scoped like the rest of `/v2/patient/me/`: the subject is the caller. One event per visit
 * (past and upcoming), order (at the date it was placed), Unspecified Referral (at its document's
 * date) and lab or imaging result (at the date it was returned), newest first. Every event is returned: the portal filters by type, provider,
 * status and time range itself.
 *
 * Data only, no wording: the portal names events and builds their context line
 * (patient-web-v2/AGENTS.md, "Patient-facing copy lives in the frontend").
 */

export const PATIENT_TIMELINE_EVENT_TYPES = [
  'visit',
  'order',
  'referral',
  'result',
  'imaging',
] as const
export const PATIENT_TIMELINE_STATUSES = [
  'upcoming',
  'awaiting-result',
  'sent',
  'completed',
  'preliminary',
] as const
export const PATIENT_TIMELINE_SOURCES = ['visits', 'orders', 'results'] as const

const timelineEventSchema = z.object({
  /** `<resourceType>/<id>`: one event per record. */
  id: z.string(),
  type: z.enum(PATIENT_TIMELINE_EVENT_TYPES),
  /** FHIR date/dateTime/instant the event happened (or, for an upcoming visit, will). */
  date: z.string(),
  status: z.enum(PATIENT_TIMELINE_STATUSES),
  /** The record's own name; null when it carries none, and the portal names it by type. */
  title: z.string().nullable(),
  /** Visit clinician, order performer (else author), or result performer. */
  provider: z.string().nullable(),
  /** A visit held by video. */
  isVideoVisit: z.boolean(),
  /** An order's or result's modality, when known. */
  modality: z.enum(['lab', 'imaging']).nullable(),
  /** The visit's reason, as booked. */
  reason: z.string().nullable(),
  /**
   * The record the event opens in the portal's document viewer, only when there is one the
   * patient may read: the visit's summary (else its signed note), the order's requisition, the
   * Unspecified Referral's PDF, or the result (its report, document or lab values).
   */
  source: z
    .object({
      section: z.enum(PATIENT_HEALTH_RECORD_SECTIONS),
      resourceType: z.enum(CHART_RECORD_RESOURCE_TYPES),
      resourceId: z.string(),
      title: z.string().nullable(),
    })
    .nullable(),
})

export const patientMeTimeline = oc
  .route({ method: 'GET', path: '/v2/patient/me/timeline' })
  .input(z.object({}))
  .output(
    z.object({
      events: z.array(timelineEventSchema),
      /** Sources that could not be read; the events from the others are still returned. */
      unavailableSources: z.array(z.enum(PATIENT_TIMELINE_SOURCES)),
    }),
  )
